import {mergeAttributes, Node} from "@tiptap/core";
import type {CommandProps} from "@tiptap/core";
import {ReactNodeViewRenderer} from "@tiptap/react";
import PrintoutRegionNodeView from "./PrintoutRegionNodeView";
import {
    findPrintoutRegion,
    PRINTOUT_FOOTER_NODE_NAME,
    PRINTOUT_HEADER_NODE_NAME,
    PRINTOUT_REGION_ATTRIBUTE,
    PRINTOUT_REGION_CLASS,
    PRINTOUT_REGION_CONTENT,
} from "./printoutRegionOptions";
import type {PrintoutRegionKind} from "./printoutRegionOptions";

declare module "@tiptap/core" {
    interface Commands<ReturnType> {
        printoutRegions: {
            setPrintoutHeader: () => ReturnType;
            unsetPrintoutHeader: () => ReturnType;
            setPrintoutFooter: () => ReturnType;
            unsetPrintoutFooter: () => ReturnType;
        };
    }
}

const insertRegion = (kind: PrintoutRegionKind, nodeName: string) => ({dispatch, state, tr}: CommandProps) => {
    if (findPrintoutRegion(state.doc, kind)) return false;

    const region = state.schema.nodes[nodeName]?.createAndFill();

    if (!region) return false;

    if (dispatch) tr.insert(kind === "header" ? 0 : state.doc.content.size, region);

    return true;
};

const removeRegion = (kind: PrintoutRegionKind) => ({dispatch, state, tr}: CommandProps) => {
    const region = findPrintoutRegion(state.doc, kind);

    if (!region) return false;

    if (dispatch) tr.delete(region.pos, region.pos + region.node.nodeSize);

    return true;
};

export const PrintoutHeaderNode = Node.create({
    name: PRINTOUT_HEADER_NODE_NAME,

    content: PRINTOUT_REGION_CONTENT,
    defining: true,
    draggable: false,
    isolating: true,
    selectable: false,

    parseHTML() {
        return [{tag: 'div[data-type="printout-header"]'}];
    },

    renderHTML({HTMLAttributes}) {
        return [
            "div",
            mergeAttributes(HTMLAttributes, {
                "data-type": "printout-header",
                [PRINTOUT_REGION_ATTRIBUTE]: "header",
                class: `${PRINTOUT_REGION_CLASS} ${PRINTOUT_REGION_CLASS}--header`,
            }),
            0,
        ];
    },

    addCommands() {
        return {
            setPrintoutHeader: () => insertRegion("header", PRINTOUT_HEADER_NODE_NAME),
            unsetPrintoutHeader: () => removeRegion("header"),
        };
    },

    addNodeView() {
        return ReactNodeViewRenderer(PrintoutRegionNodeView, {
            trackNodeViewPosition: true,
        });
    },
});

export const PrintoutFooterNode = Node.create({
    name: PRINTOUT_FOOTER_NODE_NAME,

    content: PRINTOUT_REGION_CONTENT,
    defining: true,
    draggable: false,
    isolating: true,
    selectable: false,

    parseHTML() {
        return [{tag: 'div[data-type="printout-footer"]'}];
    },

    renderHTML({HTMLAttributes}) {
        return [
            "div",
            mergeAttributes(HTMLAttributes, {
                "data-type": "printout-footer",
                [PRINTOUT_REGION_ATTRIBUTE]: "footer",
                class: `${PRINTOUT_REGION_CLASS} ${PRINTOUT_REGION_CLASS}--footer`,
            }),
            0,
        ];
    },

    addCommands() {
        return {
            setPrintoutFooter: () => insertRegion("footer", PRINTOUT_FOOTER_NODE_NAME),
            unsetPrintoutFooter: () => removeRegion("footer"),
        };
    },

    addNodeView() {
        return ReactNodeViewRenderer(PrintoutRegionNodeView, {
            trackNodeViewPosition: true,
        });
    },
});

export const PRINTOUT_REGION_EXTENSIONS = [PrintoutHeaderNode, PrintoutFooterNode];
